/** @format */

import React from "react";
import { message, Select, Table, Tag } from "antd";
import format from "date-fns/format";
import {
  AccountStatus,
  AccountType,
  IAccountsList,
  BillingStyle,
} from "../../MainContent.types";
import { CopyOutlined } from "@ant-design/icons";
import Column from "antd/lib/table/Column";
import {
  capitalizeWord,
  formatDateTime,
} from "../../../../services/UtilityService";
import AccountsOverviewBillingModal from "../../AccountsOverview/AccountsOverviewBillingModal";
import LocalStorageService from "../../../../services/LocalStorageService";
import VoiceGainAuthSvcApi from "../../../../apis/VoiceGainAuthSvcApi";

interface IAccountGeneralProps {
  accountData: IAccountsList[];
}

// Keeps the tag inside the select box - the longest label (MANUAL-REFILL)
// used to spill over the border and the dropdown arrow.
const billingTagStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  margin: 0,
  textAlign: "center",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const AccountGeneral = ({ accountData }: IAccountGeneralProps) => {
  const modifyStatus = async (status: AccountStatus) => {
    try {
      let accountId = accountData[0].accountId;
      let response = await VoiceGainAuthSvcApi.modAccount(accountId, {
        status,
      });
      if (response.ok) {
        let data = await response.json();
        message.success("Account has been modified.");
      } else throw new Error("Unable to modify account.");
    } catch (err) {
      console.log(err);
      if (err instanceof Error) {
        message.error(err.message);
      }
    }
  };

  const modifyBillingStyle = async (billingStyle: BillingStyle) => {
    try {
      let accountId = LocalStorageService.getAccountId();
      let response = await VoiceGainAuthSvcApi.modAccount(accountId, {
        billingStyle,
      });
      if (response.ok) {
        let data = await response.json();
        message.success("Billing Style has been modified.");
      } else throw new Error("Unable to modify account.");
    } catch (err) {
      console.log(err);
      if (err instanceof Error) {
        message.error(err.message);
      }
    }
  };

  return (
    <Table
      dataSource={accountData}
      rowKey={(record: IAccountsList) => record.accountId}
      scroll={{ x: 1900 }}
      size="small"
    >
      <Column title="Name" dataIndex="webName" key="webName" ellipsis={true} />
      <Column
        title="ID"
        dataIndex="accountId"
        key="accountId"
        ellipsis={true}
        render={(text) => (
          <div
            title={text}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <CopyOutlined
              // onClick={() => copyTextToClipboard(text)}
              style={{ marginRight: "0.2rem" }}
            />
            {text}
          </div>
        )}
      />
      <Column
        title="Email"
        dataIndex="creatorEmail"
        key="creatorEmail"
        // render={(id) => {
        //   const user = findAccountUser(id);
        //   if (isLoadingUsers)
        //     return <Skeleton active paragraph={{ rows: 1 }} title={false} />;
        //   else return <div>{user && user.email}</div>;
        // }}
      />
      <Column
        title="Created"
        dataIndex="dateCreated"
        key="dateCreated"
        // render={(date) => formatDateTime(date)}
        render={(date) => {
          if (date) {
            return formatDateTime(date);
          } else {
            return formatDateTime("1971-01-01T00:00:00");
          }
        }}
      />
      <Column
        title="Last Login"
        dataIndex="lastLoginDate"
        key="lastLoginDate"
        // render={(date) => formatDateTime(date)}
        render={(date) => {
          if (date) {
            return formatDateTime(date);
          } else {
            return formatDateTime("1971-01-01T00:00:00");
          }
        }}
      />
      <Column
        title="Last Used"
        dataIndex="lastUsedEpoch"
        key="lastUsedEpoch"
        render={(date) => {
          if (date) {
            const dateObject =
              date.toString().length === 10
                ? new Date(date * 1000)
                : new Date(date);
            return format(dateObject, "yyyy-MM-dd HH:mm:ss");
          } else {
            return "none";
          }
        }}
      />
      <Column
        title={
          <div
            title="Type"
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Type
          </div>
        }
        dataIndex="accountType"
        key="accountType"
        width="130px"
        render={(accountType, record) => {
          const renderColor = () => {
            switch (accountType) {
              case AccountType.SpeechWorks:
                return "orange";
              case AccountType.SpeechAnalytics:
                return "purple";
              case AccountType.Acp:
                return "blue";
            }
          };
          return (
            <Tag
              style={{ textAlign: "center", fontSize: "10px" }}
              color={renderColor()}
            >
              {accountType}
            </Tag>
          );
        }}
      />
      <Column
        title={
          <div
            title="Features"
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Features
          </div>
        }
        dataIndex="premiumFeatures"
        key="premiumFeatures"
        render={(features, record: IAccountsList) => {
          const highlightMain = (feature: string) => {
            if (feature === record.mainMode) return "green";
          };
          if (features) {
            return features.map((feature) => (
              <span key={feature}>
                <Tag
                  color={highlightMain(feature)}
                  style={{
                    width: "100%",
                    textAlign: "center",
                  }}
                >
                  {feature}
                </Tag>
              </span>
            ));
          }
        }}
      />
      <Column<IAccountsList>
        title={
          <div
            title="Number of Contexts"
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            # Contexts
          </div>
        }
        dataIndex="numberCloudContexts"
        key="contexts"
        align="center"
        sorter={(
          a: { numberCloudContexts: number },
          b: { numberCloudContexts: number }
        ) => a.numberCloudContexts - b.numberCloudContexts}
        render={(text) => {
          if (text) {
            return <Tag style={{ width: "40px" }}>{text}</Tag>;
          } else return <Tag style={{ width: "40px" }}>0</Tag>;
        }}
        filters={[
          { text: "1", value: "1" },
          { text: "2+", value: "2" },
          { text: "5+", value: "5" },
          { text: "10+", value: "10" },
        ]}
        onFilter={(value, record) => {
          switch (value) {
            case "1":
              return record.numberCloudContexts === 1;
            case "2":
              return record.numberCloudContexts >= 2;
            case "5":
              return record.numberCloudContexts >= 5;
            case "10":
              return record.numberCloudContexts >= 10;
            default:
              return true;
          }
        }}
      />
      <Column
        title={
          <div
            title="Number of Edge Deployments"
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            # Edge
          </div>
        }
        dataIndex="numberEdgeDeployments"
        key="edgeDeployments"
        align="center"
        render={(text) => {
          if (text) {
            if (parseInt(text) < 5)
              return (
                <Tag style={{ width: "40px" }} color="green">
                  {text}
                </Tag>
              );
            else if (parseInt(text) >= 5 && parseInt(text) < 10)
              return (
                <Tag style={{ width: "40px" }} color="blue">
                  {text}
                </Tag>
              );
            else if (parseInt(text) >= 10 && parseInt(text) < 15)
              return (
                <Tag style={{ width: "40px" }} color="purple">
                  {text}
                </Tag>
              );
            else if (parseInt(text) >= 15 && parseInt(text) < 20)
              return (
                <Tag style={{ width: "40px" }} color="volcano">
                  {text}
                </Tag>
              );
            else if (parseInt(text) >= 20)
              return (
                <Tag style={{ width: "40px" }} color="magenta">
                  {text}
                </Tag>
              );
          } else return <Tag style={{ width: "40px" }}>0</Tag>;
        }}
      />
      <Column
        title={
          <div
            title="Number of Websockets"
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            # Websockets
          </div>
        }
        dataIndex="numberWebsockets"
        key="webSockets"
        align="center"
        render={(text) => {
          if (text) {
            if (parseInt(text) < 5)
              return (
                <Tag style={{ width: "40px" }} color="green">
                  {text}
                </Tag>
              );
            else if (parseInt(text) >= 5 && parseInt(text) < 10)
              return (
                <Tag style={{ width: "40px" }} color="blue">
                  {text}
                </Tag>
              );
            else if (parseInt(text) >= 10 && parseInt(text) < 15)
              return (
                <Tag style={{ width: "40px" }} color="purple">
                  {text}
                </Tag>
              );
            else if (parseInt(text) >= 15 && parseInt(text) < 20)
              return (
                <Tag style={{ width: "40px" }} color="volcano">
                  {text}
                </Tag>
              );
            else if (parseInt(text) >= 20)
              return (
                <Tag style={{ width: "40px" }} color="magenta">
                  {text}
                </Tag>
              );
          } else return <Tag style={{ width: "40px" }}>0</Tag>;
        }}
      />
      <Column
        title={
          <div
            title="Number of Phone Numbers"
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            # Phone no.
          </div>
        }
        dataIndex="phoneNumbers"
        key="phoneNumbers"
        align="center"
        render={(phoneNumbers) => {
          if (phoneNumbers) {
            if (parseInt(phoneNumbers.length) < 5)
              return (
                <Tag style={{ width: "40px" }} color="green">
                  {phoneNumbers.length}
                </Tag>
              );
            else if (
              parseInt(phoneNumbers.length) >= 5 &&
              parseInt(phoneNumbers.length) < 10
            )
              return (
                <Tag style={{ width: "40px" }} color="blue">
                  {phoneNumbers.length}
                </Tag>
              );
            else if (
              parseInt(phoneNumbers.length) >= 10 &&
              parseInt(phoneNumbers.length) < 15
            )
              return (
                <Tag style={{ width: "40px" }} color="purple">
                  {phoneNumbers.length}
                </Tag>
              );
            else if (
              parseInt(phoneNumbers.length) >= 15 &&
              parseInt(phoneNumbers.length) < 20
            )
              return (
                <Tag style={{ width: "40px" }} color="volcano">
                  {phoneNumbers.length}
                </Tag>
              );
            else if (parseInt(phoneNumbers.length) >= 20)
              return (
                <Tag style={{ width: "40px" }} color="magenta">
                  {phoneNumbers.length}
                </Tag>
              );
          } else return <Tag style={{ width: "40px" }}>0</Tag>;
        }}
      />
      <Column
        title={
          <div
            title="Number of Users"
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            # Users
          </div>
        }
        dataIndex="numberUsers"
        key="numberOfUsers"
        align="center"
        render={(text) => {
          if (text) {
            if (parseInt(text) < 5)
              return (
                <Tag style={{ width: "40px" }} color="green">
                  {text}
                </Tag>
              );
            else if (parseInt(text) >= 5 && parseInt(text) < 10)
              return (
                <Tag style={{ width: "40px" }} color="blue">
                  {text}
                </Tag>
              );
            else if (parseInt(text) >= 10 && parseInt(text) < 15)
              return (
                <Tag style={{ width: "40px" }} color="purple">
                  {text}
                </Tag>
              );
            else if (parseInt(text) >= 15 && parseInt(text) < 20)
              return (
                <Tag style={{ width: "40px" }} color="volcano">
                  {text}
                </Tag>
              );
            else if (parseInt(text) >= 20)
              return (
                <Tag style={{ width: "40px" }} color="magenta">
                  {text}
                </Tag>
              );
          } else return <Tag style={{ width: "40px" }}>0</Tag>;
        }}
      />
      <Column
        title={
          <div
            title="Balance"
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Balance
          </div>
        }
        dataIndex="billingAccountId"
        key="currentBalance"
        align="center"
        render={(text, record: IAccountsList) => {
          return (
            <AccountsOverviewBillingModal
              billingId={text}
              billingStyle={record.billingStyle}
              lastRefill={record.lastRefill}
              currentBalance={record.currentBalance}
            />
          );
        }}
      />
      <Column
        width={180}
        title={
          <div
            title="Billing"
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Billing
          </div>
        }
        dataIndex="billingStyle"
        key="billingStyle"
        render={(billingStyle, record: IAccountsList) => {
          return (
            <Select
              defaultValue={
                billingStyle ? billingStyle : BillingStyle.ManualRefill
              }
              style={{ width: "100%" }}
              onChange={modifyBillingStyle}
            >
              <Select.Option key={BillingStyle.ManualRefill}>
                <Tag style={billingTagStyle} color="green">
                  {capitalizeWord(BillingStyle.ManualRefill)}
                </Tag>
              </Select.Option>
              <Select.Option key={BillingStyle.AutoRefill}>
                <Tag style={billingTagStyle} color="gold">
                  {capitalizeWord(BillingStyle.AutoRefill)}
                </Tag>
              </Select.Option>
              <Select.Option key={BillingStyle.Invoice}>
                <Tag style={billingTagStyle} color="red">
                  {capitalizeWord(BillingStyle.Invoice)}
                </Tag>
              </Select.Option>
            </Select>
          );
        }}
      />
      <Column
        width={140}
        title="Status"
        dataIndex="status"
        key="status"
        align="center"
        render={(status, record: IAccountsList) => {
          return (
            <Select
              defaultValue={status ? status : AccountStatus.Active}
              style={{ width: "100%" }}
              onChange={modifyStatus}
            >
              <Select.Option key={AccountStatus.Active}>
                <Tag
                  style={{ width: "90%", textAlign: "center" }}
                  color="green"
                >
                  {capitalizeWord(AccountStatus.Active)}
                </Tag>
              </Select.Option>
              <Select.Option key={AccountStatus.Pending}>
                <Tag style={{ width: "90%", textAlign: "center" }} color="gold">
                  {capitalizeWord(AccountStatus.Pending)}
                </Tag>
              </Select.Option>
              <Select.Option key={AccountStatus.Suspended}>
                <Tag style={{ width: "90%", textAlign: "center" }} color="red">
                  {capitalizeWord(AccountStatus.Suspended)}
                </Tag>
              </Select.Option>
            </Select>
          );
        }}
      />
      <Column
        title="PCI-DSS"
        dataIndex="pciDss"
        key="pciDss"
        align="center"
        render={(pciDss) => (
          <Tag color={pciDss == true ? "green" : "red"} key={pciDss}>
            {pciDss.toString()}
          </Tag>
        )}
      />
    </Table>
  );
};

export default AccountGeneral;
